import React, { useRef, useState } from 'react'
import { FaRegUser } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { useCreateNewProfileMutation, usePatchCollaboratorMutation } from '../../../redux/appSlices/collaborator/collaboratorApiSlice';
import { getCollaborator } from '../../../redux/appSlices/collaborator/collaboratorSlice';
import { toast } from 'react-toastify';
import ProfileSwitchOverlay from '../../UI/ProfileSwitchOverlay';
import { translation } from '../../../translation';
import { getProfileEntryId, getFirstProfileId, buildMainProfilePreview } from '../../../utils/profilePicture';

const MIN_PROFILE_SWITCH_OVERLAY_MS = 550;

const CreateNewProfile = ({ setOpenEditPopup }) => {

    const dispatch = useDispatch()

    const collaborator = useSelector(
        (state) => state.collaborator.collaboratorInfo
    );
    const lang = useSelector(
        (state) => state.collaborator.collaboratorInfo.language
    );

    // useState
    const [openSwitchProfileModal, setOpenSwitchProfileModal] = useState(false)
    const [popupMessage, setPopupMessag] = useState(' ')
    const [fromProfile, setFromProfile] = useState(null)
    const [toProfiles, setToProfiles] = useState(null)
    const profileSwitchStartedAtRef = useRef(0)

    const finishProfileSwitchOverlay = (callback) => {
        const elapsed = Date.now() - profileSwitchStartedAtRef.current
        const remaining = Math.max(0, MIN_PROFILE_SWITCH_OVERLAY_MS - elapsed)
        window.setTimeout(callback, remaining)
    }


    //   API CALLS
    const [createNewProfile] = useCreateNewProfileMutation()
    const [patchCollaborator, { isLoading }] = usePatchCollaboratorMutation();

    // FUNCTIONS
    const handleCreateNewProfile = () => {
        setPopupMessag(translation.settings.switchProfile.createProfile[lang])
        profileSwitchStartedAtRef.current = Date.now()
        setOpenSwitchProfileModal(true)
        createNewProfile({
            collaborator_id: collaborator.collaborator_id
        })
            .unwrap()
            .then(() => {
                finishProfileSwitchOverlay(() => {
                    setOpenSwitchProfileModal(false)
                    setOpenEditPopup(false)
                })
            })
            .catch((err) => {
                toast.error(err?.data?.message?.[lang]);
                setOpenSwitchProfileModal(false)
            })
    }

    const handleSwitchProfile = (id) => {
        const profiles = collaborator.profiles || [];

        if (id === 'both' && profiles.length < 2) {
            return null
        }

        if (id === 'both' && collaborator.profileDisplayMode === 'both') {
            setOpenEditPopup(false)
            return null
        }

        if (
            id !== 'both' &&
            collaborator.profileDisplayMode !== 'both' &&
            id === collaborator?.currentProfile?._id
        ) {
            setOpenEditPopup(false)
            return null
        }

        // Get current profile info
        const currentProfileId =
            getProfileEntryId(collaborator?.currentProfile) ||
            getFirstProfileId(collaborator);
        const currentProfile =
            profiles.find(
                (profile) => getProfileEntryId(profile) === currentProfileId,
            ) || profiles[0];

        let targetProfiles = null;
        let message = translation.settings.switchProfile.switchProfile[lang];
        let profileDisplayMode = 'single';
        let profileToSet = id;

        // Handle "both" selection
        if (id === 'both') {
            targetProfiles = profiles.slice(0, 2);
            profileDisplayMode = 'both';
            profileToSet = null;
            message = lang === 'ar' 
                ? 'جاري تفعيل كلا الحسابين...'
                : lang === 'en'
                ? 'Activating both profiles...'
                : 'Activation des deux profils...';
        } else {
            targetProfiles = [
                profiles.find(
                    (profile) => getProfileEntryId(profile) === id,
                ),
            ];
            profileDisplayMode = 'single';
            profileToSet = id;
        }

        setFromProfile(currentProfile);
        setToProfiles(targetProfiles);
        setPopupMessag(message);
        profileSwitchStartedAtRef.current = Date.now()
        setOpenSwitchProfileModal(true);

        patchCollaborator({
            id: collaborator._id,
            content: { 
                currentProfile: profileToSet,
                profileDisplayMode: profileDisplayMode
            }
        })
            .unwrap()
            .then((res) => {
                if (profileDisplayMode === 'both') {
                    const firstProfileId = getFirstProfileId(collaborator);
                    if (firstProfileId) {
                    localStorage.setItem(
                        `publicActiveProfile_${collaborator.collaborator_id}`,
                        firstProfileId,
                    );
                    }
                } else if (profileToSet) {
                    localStorage.setItem(
                        `publicActiveProfile_${collaborator.collaborator_id}`,
                        profileToSet,
                    );
                }

                finishProfileSwitchOverlay(() => {
                    setOpenSwitchProfileModal(false);
                    setOpenEditPopup(false);
                    setFromProfile(null);
                    setToProfiles(null);
                });

            })
            .catch((err) => {
                toast.error(err?.data?.message?.[lang]);
                setOpenSwitchProfileModal(false);
                setFromProfile(null);
                setToProfiles(null);
            })
    }

    const isBothMode = collaborator?.profileDisplayMode === 'both';
    const rawProfiles = collaborator.profiles || [];
    const profiles = rawProfiles.map((profile, index) => {
        if (typeof profile === 'object' && profile !== null) return profile;

        const profileId = getProfileEntryId(profile);
        if (
            index === 0 ||
            profileId === getFirstProfileId(collaborator)
        ) {
            return buildMainProfilePreview(collaborator);
        }

        return { _id: profileId };
    });
    const bothModeAvatars = profiles.slice(0, 2);

    const renderProfileRow = (profile, { isSelected, onSelect }) => (
        <div
            key={getProfileEntryId(profile)}
            onClick={onSelect}
            className={`flex justify-between items-center mt-3 rounded-[10px] px-3 py-2 cursor-pointer ${isSelected ? 'border border-labels' : 'border border-gray-200'}`}
        >
            <div className="flex items-center">
                <div className={`w-9 h-9 rounded-full ${!profile.profile_picture && 'border border-labels'}`}>
                    {profile.profile_picture && (
                        <img
                            src={`${process.env.REACT_APP_IMAGE_PATH}${profile?.profile_picture}`}
                            className="object-cover h-full w-full rounded-full"
                            alt="profile"
                        />
                    )}
                </div>
                <div className={lang === 'ar' ? 'pr-4' : 'pl-4'}>
                    <h3 className="font-SteradianMedium text-[18px] tracking-[-.97px]">
                        {profile?.first_name || '-'} {profile?.last_name || '-'}
                    </h3>
                    <p className="text-[14px] text-labels tracking-[-.65px]">
                        {profile?.title?.[lang] || '-'}
                    </p>
                </div>
            </div>
            <div className={`h-4 w-4 rounded-full ${isSelected ? 'bg-labels' : 'border border-labels'}`} />
        </div>
    );

    return (
        <div>
            {/* <div className={`text-2xl mt-[2rem] ${lang === 'ar' ? 'font-lalezarRegular' : 'font-SteradianMedium'}`}>
                {translation.settings.switchProfile.title[lang]}
            </div> */}

            <div className="text-[18px] mt-[2rem]">
                {translation.settings.switchProfile.subtitle[lang]}
            </div>

            {/* Both Profiles Option — only when user has 2 profiles */}
            {profiles.length >= 2 && (
            <div
                onClick={() => handleSwitchProfile('both')}
                className={`flex justify-between items-center mt-3 rounded-[10px] px-3 py-2 cursor-pointer ${collaborator?.profileDisplayMode === 'both' ? 'border border-labels' : 'border border-gray-200'}`}
            >
                <div className="flex items-center">
                    <div className="flex -space-x-2">
                        {bothModeAvatars.map((profile) => (
                            <div key={profile?._id} className={`w-9 h-9 rounded-full border-2 border-white ${!profile.profile_picture && 'bg-gray-100 flex items-center justify-center'}`}>
                                {profile.profile_picture ? (
                                    <img 
                                        src={`${process.env.REACT_APP_IMAGE_PATH}${profile?.profile_picture}`} 
                                        className="object-cover h-full w-full rounded-full" 
                                        alt="profile"
                                    />
                                ) : (
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className={lang === 'ar' ? 'pr-4' : 'pl-4'}>
                        <h3 className="font-SteradianMedium text-[18px] tracking-[-.97px]">
                            {lang === 'ar' ? 'كلا الحسابين' : lang === 'en' ? 'Both profiles' : 'Les deux profils'}
                        </h3>
                        <p className="text-[14px] text-labels tracking-[-.65px]">
                            {lang === 'ar' ? 'عرض كلا الملفين الشخصيين' : lang === 'en' ? 'Show both profiles' : 'Afficher les deux profils'}
                        </p>
                    </div>
                </div>
                <div className={`h-4 w-4 rounded-full ${collaborator?.profileDisplayMode === 'both' ? 'bg-labels' : 'border border-labels'}`} />
            </div>
            )}

            {profiles.map((profile) => {
                const profileId = getProfileEntryId(profile);
                const firstProfileId = getFirstProfileId(collaborator);
                const currentProfileId = getProfileEntryId(collaborator?.currentProfile);
                const isProfileSelected =
                    !isBothMode &&
                    ((currentProfileId &&
                        profileId === currentProfileId) ||
                        (!collaborator?.currentProfile &&
                            profileId === firstProfileId));

                return renderProfileRow(profile, {
                    isSelected: isProfileSelected,
                    onSelect: () => handleSwitchProfile(profileId),
                });
            })}



            {
                collaborator.profiles.length < 2 &&
                <div
                    onClick={handleCreateNewProfile}
                    className="flex text-labels gap-3 border border-dashed border-labels bg-labelsBg rounded-md py-2 items-center justify-center mt-5 cursor-pointer"
                >
                    <FaRegUser className="text-lg text-labels" />
                    <span>{translation.settings.switchProfile.buttonNewProfile[lang]}</span>
                </div>
            }


            <ProfileSwitchOverlay
                isOpen={openSwitchProfileModal}
                message={popupMessage}
                fromProfile={fromProfile}
                toProfiles={toProfiles}
            />
        </div>
    )
}

export default CreateNewProfile